import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { WeatherContext, WeatherContextService } from 'src/app/services/weather/weather-context.service';

@Component({
  selector: 'app-weather-context',
  templateUrl: './weather-context.component.html',
  styleUrls: ['./weather-context.component.scss'],
})
export class WeatherContextComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() latitude?: number | null;
  @Input() longitude?: number | null;
  /** Optional human-readable name; coordinates remain the guaranteed fallback. */
  @Input() locationName = '';
  @Input() label = 'Local weather';
  @Input() floating = false;
  @Input() compact = false;
  @Input() showRefresh = true;
  @Input() notice = '';
  /** Visible cards fetch automatically; off-screen slider cards wait until they are visible. */
  @Input() autoLoad = true;
  @ViewChild('weatherCard', { static: false }) weatherCard?: ElementRef<HTMLElement>;

  weather: WeatherContext | null = null;
  city = '';
  loading = false;
  error: string | null = null;
  private visibilityObserver?: IntersectionObserver;

  constructor(public readonly weatherService: WeatherContextService) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (!this.autoLoad) return;
    const target = this.weatherCard?.nativeElement;
    if (!target || typeof IntersectionObserver === 'undefined') {
      void this.refresh();
      return;
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = undefined;
      void this.refresh();
    }, { threshold: 0.3 });
    this.visibilityObserver.observe(target);
  }

  ngOnDestroy(): void {
    this.visibilityObserver?.disconnect();
  }

  async refresh(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.error = null;
    try {
      this.weather = this.hasCoordinates()
        ? await this.weatherService.loadForCoordinates(Number(this.latitude), Number(this.longitude), true)
        : await this.weatherService.refreshCurrentLocation();
      this.city = this.locationName.trim() || await this.weatherService.cityForWeather(this.weather);
      if (!this.weather) this.error = this.weatherService.error$.value || 'Weather is unavailable right now.';
    } finally {
      this.loading = false;
    }
  }

  iconFor(code?: string): string {
    return this.weatherService.iconName(this.weather ? { ...this.weather, conditionCode: code || this.weather.conditionCode } : null);
  }

  formattedTemperature(value?: number): string {
    return Number.isFinite(value) ? `${Math.round(Number(value))}°` : '—';
  }

  locationText(): string {
    const latitude = this.weather?.latitude ?? this.latitude;
    const longitude = this.weather?.longitude ?? this.longitude;
    if (this.locationName.trim()) return this.locationName.trim();
    if (this.city) return this.city;
    if (Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude))) return 'Finding city…';
    return 'Current location';
  }

  private hasCoordinates(): boolean {
    return Number.isFinite(Number(this.latitude)) && Number.isFinite(Number(this.longitude));
  }
}
